import {sleep} from '@benchling/js-utils';
import * as d3 from 'd3';
import $ from 'jquery';
import _ from 'underscore';

import {assert} from 'Asserts';
import {ProteinAlignment, AaSequenceAlignmentRow, ProteinAlignmentKind} from 'constants/AlignmentConstants';
import {
  ALIGNMENT_TYPE,
  SliceComponentName,
  PROTEIN_ALIGNMENT_ROW_Y_COMPONENTS,
  PROTEIN_ALIGNMENT_ROW_Z_COMPONENTS,
  SliceMapType,
} from 'constants/MapsConstants';
import Slice from 'molbio/maps/linear/Slice';
import {BasicSliceView, SliceView} from 'molbio/maps/linear/SliceView';
import Type from 'molbio/maps/Type';
import ProteinAlignmentTabState from 'molbio/tabstates/AaSequenceAlignmentTabState';
import {VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
import {capitalize} from 'util/NameUtil';
import {getSVGHeight, appendSVGToSVG, fixAxis} from 'util/SVGUtil';

// Treat special amino acids as regular amino acids
export function aminoAcidMatches(aa1: string, aa2: string): boolean {
  if (aa1 === '-' || aa2 === '-') {
    return false;
  } else {
    return aa1.toUpperCase() === aa2.toUpperCase();
  }
}

export function getIdentityValues(aminoAcidMatrix: Array<Array<string>>): Array<number> {
  assert(Array.isArray(aminoAcidMatrix), 'Invalid argument type');
  const aminoAcidsByIndex: Array<Array<string>> = _.zip(...aminoAcidMatrix);
  return aminoAcidsByIndex.map((aminoAcids) => {
    const [referenceAminoAcid, ...nonReferenceAminoAcids] = aminoAcids;
    const numVotes = nonReferenceAminoAcids.filter((aa) => aminoAcidMatches(aa, referenceAminoAcid)).length;
    return numVotes / (aminoAcidMatrix.length - 1);
  });
}

function fastaFormatRowSequence(row: AaSequenceAlignmentRow): string {
  const fastaLines = row.proteinVersion.aminoAcids.match(/.{1,80}/g);
  return fastaLines == null ? '' : fastaLines.join('\n');
}

export function getNonConsensusRows(alignment: ProteinAlignment): Array<AaSequenceAlignmentRow> {
  return alignment.alignmentType === ALIGNMENT_TYPE.CONSENSUS ? alignment.rows.slice(1) : alignment.rows;
}

export function fastaFormat(alignment: ProteinAlignment): string {
  // Don't output consensus row, as other programs may confuse it for an actual AA sequence
  return getNonConsensusRows(alignment)
    .map((row) => `>${row.name}\n${fastaFormatRowSequence(row)}\n`)
    .join('\n');
}

export function makeSliceView({
  alignmentRowType,
  tabState,
  slice,
  visibilityOptions,
  row,
  forAlignmentPNGExport,
}: {
  alignmentRowType: ProteinAlignmentKind | null;
  tabState: ProteinAlignmentTabState;
  slice: Slice;
  visibilityOptions: VisibilityOptions;
  row: AaSequenceAlignmentRow;
  forAlignmentPNGExport: boolean;
}): SliceView {
  const y = PROTEIN_ALIGNMENT_ROW_Y_COMPONENTS.filter(
    (component) => component !== SliceComponentName.proteinAlignmentIdentityGraph || alignmentRowType
  );
  const z = PROTEIN_ALIGNMENT_ROW_Z_COMPONENTS.filter(
    (component) => component !== SliceComponentName.proteinAlignmentIdentityGraph || alignmentRowType
  );
  return new BasicSliceView({
    slice,
    selection: tabState.get('selection'),
    tabstate: tabState,
    visibilityOptions,
    mapType: SliceMapType.proteinAlignment,
    model: tabState,
    data: {row},
    // @ts-expect-error: possible extraneous arg
    polymerResidues: row.proteinVersion.aminoAcids,
    y,
    z,
    el: BasicSliceView.createSVGElement(),
    forAlignmentPNGExport,
    range: tabState.get('range'),
  });
}

export function makeSliceFromSelection(tabState: ProteinAlignmentTabState): Slice {
  const end = tabState.get('selection').end();
  if (end == null) {
    return tabState.get('slice');
  } else {
    const start = tabState.get('selection').start();
    const type = tabState.getType();
    const width = type.width(end - start) + type.letterSpacing;

    const x = d3.scaleLinear().clamp(true).domain([start, end]).range([0, width]);
    const xInv = d3.scaleLinear().clamp(true).domain([0, width]).range([start, end]);
    return new Slice({
      start,
      end,
      width,
      maxWidth: tabState.get('slice').get('maxWidth'),
      visibleWidth: tabState.get('slice').get('visibleWidth'),
      x,
      xInv,
    });
  }
}

function renderAlignmentSliceSVGs(tabState: ProteinAlignmentTabState): Array<SVGSVGElement> {
  const alignment: ProteinAlignment = tabState.get('alignment')!;
  const sliceViews = alignment.rows.map((row, index) => {
    const alignmentRowType = index === 0 ? alignment.alignmentType : null;
    return makeSliceView({
      alignmentRowType,
      tabState,
      slice: makeSliceFromSelection(tabState),
      visibilityOptions: tabState.get('localVisibilityOptionsList')![index],
      row,
      forAlignmentPNGExport: true,
    });
  });
  const svgs = [];
  for (const sliceView of sliceViews) {
    sliceView.render();
    sliceView.killSelection();
    const svg = sliceView.el;
    d3.select(svg).attr('height', sliceView.boxHeight() + 20);

    // Prepend the name and type of the slice.
    const nameType = new Type(12);
    const proteinName = d3
      .select(svg)
      .insert('svg:g', ':first-child')
      .append('svg:text')
      .attr('font-size', nameType.fontSize)
      .attr('text-anchor', 'begin')
      .attr('dy', nameType.height())
      .attr('x', 20);
    const aaSequenceType = proteinName.append('svg:tspan').attr('font-weight', 900);
    /* eslint-disable benchlint/no-eqeqeq-null */
    const isConsensusMaster =
      (sliceView.data!.row as AaSequenceAlignmentRow).originalProteinId === null &&
      alignment.alignmentType === ALIGNMENT_TYPE.CONSENSUS;
    /* eslint-enable benchlint/no-eqeqeq-null */
    const isTemplateMaster =
      (sliceView.data!.row as AaSequenceAlignmentRow).name !== sliceView.model.get('alignment').name &&
      alignment.alignmentType === 'TEMPLATE';
    if (isConsensusMaster || isTemplateMaster) {
      aaSequenceType.text(`${alignment.alignmentType.toLowerCase()} sequence `);
    } else {
      aaSequenceType.text(`aligned sequence `);
    }

    proteinName
      .append('svg:tspan')
      .attr('dx', nameType.charWidth())
      .text((sliceView.data!.row as AaSequenceAlignmentRow).name);
    proteinName.attr(
      'width',
      nameType.charWidth() *
        ((sliceView.data!.row as AaSequenceAlignmentRow).name + aaSequenceType.text()).length
    );

    fixAxis(svg);
    svgs.push(svg);
  }
  return svgs;
}

export async function renderAlignmentSVG(tabState: ProteinAlignmentTabState): Promise<SVGSVGElement> {
  // Renders AA sequence alignment
  const sliceSVGs = renderAlignmentSliceSVGs(tabState);

  // Antibody-related layers (numbering, CDRs, liability sites) fetch their own data and so need a chance to
  // rerender themselves. This is essentially the same logic as test/lib/waitForCallbacks, duplicated here to
  // avoid coupling test and prod code.
  for (let i = 0; i < 4; i++) {
    await sleep(0);
  }

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  d3.select(svg).attr('xmlns', 'http://www.w3.org/2000/svg');
  // Work-around: To add an opaque white background to the SVG when it is exported to PNG, we can configure
  // Inkscape with a sodipodi:namedview element. D3 drops custom XML namespaces, so we need to add the
  // element with jQuery. https://stackoverflow.com/q/28423464
  $(svg)
    .attr('xmlns:sodipodi', 'http://sodipodi.sourceforge.net/DTD/sodipodi-0.dtd')
    .attr('xmlns:inkscape', 'http://www.inkscape.org/namespaces/inkscape')
    .append("<sodipodi:namedview inkscape:pageopacity='1' pagecolor='#ffffff'/>");

  const titleType = new Type(16);
  const alignment = tabState.get('alignment')!;
  const alignmentType = capitalize(alignment.alignmentType);
  d3.select(svg)
    .append('svg:text')
    .attr('font-size', titleType.fontSize)
    .attr('font-weight', 900)
    .attr('dy', titleType.height())
    .text(`${alignmentType} Alignment: ${alignment.name}`);

  const maxTextWidth = Math.max(...sliceSVGs.map((slice) => parseInt($('g text', slice).attr('width'), 10)));
  let lowestY = titleType.height() + 20;
  for (const sliceSVG of sliceSVGs) {
    // Position label in front of slice.
    d3.select(sliceSVG)
      .select('g text')
      .attr('transform', `translate(-${maxTextWidth + 40},0)`);
    appendSVGToSVG({
      parentSVG: svg,
      childSVG: sliceSVG,
      transform: `translate(${maxTextWidth + 20}, ${lowestY})`,
    });
    lowestY += getSVGHeight(sliceSVG);
  }
  return svg;
}
